import type { UserField, UserRoleMap } from "types/UserZendesk";
import type { User } from "types/UserZendesk";

import {
  createContext,
  useState,
  useContext,
  type ReactNode,
  useEffect
} from 'react';
import {
  getSelectedUser,
  saveSelectedUser,
  removeSelectedUser
} from '@storage/userStorage';
export interface NewUserProps {
  name: string;
  cpf_3: string;
  cnpj_regex: string;
  phone: string;
  email: string;
  tipoConta: string;
}

interface UserContextType {
  selectedUser: User | null;
  setSelectedUser: (user: User | null) => void;
  updateUser: (partial: Partial<User>) => void;

  userFields: UserField[];
  setUserFields: (fields: UserField[]) => void;

  usersInfo: User[];
  setUsersInfo: (users: User[]) => void;

  userIdRoles: UserRoleMap[];
  setUserIdRoles: (roles: UserRoleMap[]) => void;

  urlCustom: string[];
  setUrlCustom: (urls: string[]) => void;

  selectedRole: string | null;
  setSelectedRole: (role: string | null) => void;

  newUserFormData: NewUserProps;
  setNewUserFormData: (data: NewUserProps) => void;

  customFormFields: Record<string, any>;
  setCustomFormFields: (fields: Record<string, any>) => void;
  updateCustomFormField: (fieldKey: string, value: any) => void;

  currentTipoConta: string | undefined;
  setCurrentTipoConta: (tipoConta: string | undefined) => void;

  userRoles: string;
  setUserRoles: (roles: string) => void;
}

const UserContext = createContext<UserContextType | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
  const [selectedUser, setSelectedUserState] = useState<User | null>(null);
  const [usersInfo, setUsersInfo] = useState<User[]>([]);
  const [userIdRoles, setUserIdRoles] = useState<UserRoleMap[]>([]);
  const [urlCustom, setUrlCustom] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [userFields, setUserFields] = useState<UserField[]>([]);
  const [customFormFields, setCustomFormFields] = useState<Record<string, any>>({});
  const [currentTipoConta, setCurrentTipoConta] =  useState<string | undefined>();
  const [userRoles, setUserRoles] = useState<string>('');
  const [newUserFormData, setNewUserFormData] = useState<NewUserProps>({
    name: '',
    cpf_3: '',
    phone: '',
    email: '',
    cnpj_regex: '',
    tipoConta: ''
  });

  useEffect(() => {
    const storedUser = getSelectedUser();
    if (storedUser) {
      setSelectedUserState(storedUser);
    }
  }, []);

  const setSelectedUser = (user: User | null) => {
    if (user) {
      saveSelectedUser(user);
    } else {
      removeSelectedUser();
    }
    setSelectedUserState(user);
  };

  const updateUser = (partial: Partial<User>) => {
    setSelectedUserState((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...partial };
      saveSelectedUser(updated);
      return updated;
    });
  };

  const updateCustomFormField = (fieldKey: string, value: any) => {
    setCustomFormFields(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  return (
    <UserContext.Provider
      value={{
        selectedUser,
        setSelectedUser,
        updateUser,
        usersInfo,
        setUsersInfo,
        userIdRoles,
        setUserIdRoles,
        urlCustom,
        setUrlCustom,
        selectedRole,
        setSelectedRole,
        newUserFormData,
        setNewUserFormData,
        userFields,
        setUserFields,
        updateCustomFormField,
        customFormFields,
        setCustomFormFields,
        currentTipoConta,
        setCurrentTipoConta,
        userRoles,
        setUserRoles
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}